import React, { useEffect, useState } from 'react';
import { CheckIcon, ChevronDownIcon, MapPinIcon } from 'lucide-react';
type MarketDefinition = {
  id: string;
  label: string;
  position: string;
  groups: {
    label?: string;
    places: string[];
  }[];
};

// Regions of the world, each broken down into the countries — and for North
// America, the states and provinces — inventory can be sold into.
const markets: MarketDefinition[] = [{
  id: 'north-america',
  label: 'North America',
  position: 'left-[20%] top-[40%]',
  groups: [{
    label: 'United States',
    places: ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia', 'Puerto Rico', 'U.S. Virgin Islands', 'Guam']
  }, {
    label: 'Canada',
    places: ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon']
  }, {
    label: 'Mexico',
    places: ['Mexico']
  }]
}, {
  id: 'latin-america',
  label: 'Latin America',
  position: 'left-[30%] top-[72%]',
  groups: [{
    places: ['Argentina', 'Brazil', 'Chile', 'Colombia', 'Costa Rica', 'Dominican Republic', 'Ecuador', 'Guatemala', 'Panama', 'Peru', 'Uruguay']
  }]
}, {
  id: 'europe',
  label: 'Europe',
  position: 'left-[53%] top-[33%]',
  groups: [{
    label: 'European Union',
    places: ['Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czechia', 'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'Ireland', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden']
  }, {
    label: 'Non-EU Europe',
    places: ['United Kingdom', 'Norway', 'Switzerland', 'Iceland', 'Serbia', 'Ukraine']
  }]
}, {
  id: 'middle-east',
  label: 'Middle East',
  position: 'left-[62%] top-[53%]',
  groups: [{
    places: ['Bahrain', 'Israel', 'Jordan', 'Kuwait', 'Oman', 'Qatar', 'Saudi Arabia', 'Turkey', 'United Arab Emirates']
  }]
}, {
  id: 'africa',
  label: 'Africa',
  position: 'left-[54%] top-[68%]',
  groups: [{
    places: ['Egypt', 'Ghana', 'Ivory Coast', 'Kenya', 'Morocco', 'Nigeria', 'Rwanda', 'Senegal', 'South Africa', 'Tanzania']
  }]
}, {
  id: 'asia',
  label: 'Asia',
  position: 'left-[77%] top-[47%]',
  groups: [{
    label: 'East Asia',
    places: ['Japan', 'South Korea', 'Taiwan', 'Hong Kong', 'China']
  }, {
    label: 'Southeast Asia',
    places: ['Singapore', 'Malaysia', 'Thailand', 'Indonesia', 'Philippines', 'Vietnam']
  }, {
    label: 'South & Central Asia',
    places: ['India', 'Sri Lanka', 'Kazakhstan']
  }]
}, {
  id: 'oceania',
  label: 'Oceania',
  position: 'left-[86%] top-[80%]',
  groups: [{
    places: ['Australia', 'New Zealand', 'Fiji']
  }]
}];

// Some brands only distribute into a defined set of markets.
const brandMarketDefaults: Record<string, {
  regions: string[];
  keep: Record<string, string[]>;
}> = {
  Nisolo: {
    regions: ['north-america', 'europe', 'asia', 'oceania'],
    keep: {
      asia: ['Japan', 'South Korea', 'Singapore'],
      oceania: ['Australia', 'New Zealand']
    }
  }
};
function placesIn(marketId: string) {
  return markets.find(market => market.id === marketId)?.groups.flatMap(group => group.places) ?? [];
}
export function GeographySelector({
  brand
}: {
  brand?: string;
}) {
  const preset = brand ? brandMarketDefaults[brand] : undefined;
  const [marketsEnabled, setMarketsEnabled] = useState<string[]>(preset?.regions ?? markets.map(market => market.id));
  const [openMarket, setOpenMarket] = useState<string | null>(null);
  // Places switched off inside an otherwise enabled region.
  const [excluded, setExcluded] = useState<Record<string, string[]>>(() => {
    if (!preset) return {};
    return Object.fromEntries(Object.entries(preset.keep).map(([marketId, keep]) => [marketId, placesIn(marketId).filter(place => !keep.includes(place))]));
  });

  // Switching brands reloads that brand's distribution footprint.
  useEffect(() => {
    setMarketsEnabled(preset?.regions ?? markets.map(market => market.id));
    setExcluded(preset ? Object.fromEntries(Object.entries(preset.keep).map(([marketId, keep]) => [marketId, placesIn(marketId).filter(place => !keep.includes(place))])) : {});
  }, [brand]);
  const toggleMarket = (market: string) => {
    setMarketsEnabled(current => current.includes(market) ? current.filter(item => item !== market) : [...current, market]);
  };
  const togglePlace = (market: string, place: string) => {
    setExcluded(current => {
      const list = current[market] ?? [];
      return {
        ...current,
        [market]: list.includes(place) ? list.filter(item => item !== place) : [...list, place]
      };
    });
  };
  const setAllPlaces = (market: MarketDefinition, enabled: boolean) => {
    setExcluded(current => ({
      ...current,
      [market.id]: enabled ? [] : market.groups.flatMap(group => group.places)
    }));
  };
  return <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="geography-heading" data-id="element-1204" data-mp="1ceadepeovpa8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" data-id="element-1205" data-mp="1hg9hk1i96q88">
        <div data-id="element-1206" data-mp="1me17ipzc829s">
          <div className="flex items-center gap-2" data-id="element-1207" data-mp="1mbf3v1fki6wu">
            <MapPinIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1208" data-mp="1rbb8n2w3a2dg" />
            <h2 id="geography-heading" className="text-lg font-semibold" data-id="element-1209" data-mp="tp41bl8ehvxe">Market access</h2>
          </div>
          <p className="mt-1 text-sm leading-relaxed text-ink/60" data-id="element-1210" data-mp="ymvx4qc6226v">Choose the regions where we can market and sell your approved inventory.</p>
        </div>
        <span className="text-sm font-medium text-ink/55" data-id="element-1211" data-mp="13kxd32eaple4">{marketsEnabled.length} of {markets.length} regions active</span>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-ink/8 bg-[#ece8df] px-3 py-4 sm:px-6 sm:py-6" data-id="element-1212" data-mp="aymfm0lzk0lj">
        <div className="relative mx-auto aspect-[2.15/1] w-full" role="group" aria-label="Interactive world market map" data-id="element-1213" data-mp="fw5sox6y67bw">
          <img src="https://cdn.magicpatterns.com/uploads/767dGYamSwJgJFZTEhNNfX/world.svg" alt="World map showing regions where inventory can be sold" className="h-full w-full object-contain opacity-90" data-id="element-1214" data-mp="2t6xiduyp4ky3" />
          {markets.map(market => {
          const active = marketsEnabled.includes(market.id);
          const excludedCount = (excluded[market.id] ?? []).length;
          return <button key={market.id} type="button" onClick={() => toggleMarket(market.id)} aria-pressed={active} aria-label={`Toggle ${market.label}`} className={`absolute ${market.position} -translate-x-1/2 -translate-y-1/2 rounded-full border px-2.5 py-1 text-xs font-semibold shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/30 sm:px-3 sm:py-1.5 ${active ? 'border-[#BB5E40] bg-[#BB5E40] text-cream hover:bg-[#94452E]' : 'border-dashed border-ink/25 bg-[#d9d5cc] text-ink/45 hover:text-ink/70'}`} data-id="element-1215" data-mp="25i4qc9l72x8r">
                <span className="flex items-center gap-1.5" data-id="element-1216" data-mp="31ftft2l7x286">
                  <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-cream' : 'bg-ink/25'}`} data-id="element-1217" data-mp="36e4qtgn91irz" />
                  {market.label}
                  {active && excludedCount > 0 && <span className="rounded-full bg-cream/25 px-1.5 text-[10px] font-semibold" data-id="element-1218" data-mp="2rndz08lnqjov">−{excludedCount}</span>}
                </span>
              </button>;
        })}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" aria-label="Available regions" data-id="element-1219" data-mp="2nn3tex1s1vaj">
        {markets.map(market => {
        const active = marketsEnabled.includes(market.id);
        return <button key={market.id} type="button" onClick={() => toggleMarket(market.id)} aria-pressed={active} className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold transition-colors ${active ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/10 bg-cream text-ink/65 hover:border-[#BB5E40]/35'}`} data-id="element-1220" data-mp="2dpz5ossa9rrm">
              {active && <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" data-id="element-1221" data-mp="1z53cu68n9yt8" />}
              {market.label}
            </button>;
      })}
      </div>

      <div className="mt-6 border-t border-ink/10" data-id="element-1222" data-mp="290etiu3m5g3m">
        {markets.map(market => {
        const enabled = marketsEnabled.includes(market.id);
        const excludedList = excluded[market.id] ?? [];
        const total = market.groups.reduce((sum, group) => sum + group.places.length, 0);
        const open = openMarket === market.id;
        return <div key={market.id} className="border-b border-ink/10 py-5" data-id="element-1223" data-mp="2rs7fi4k9m11u">
              <button type="button" onClick={() => setOpenMarket(open ? null : market.id)} aria-expanded={open} aria-controls={`${market.id}-rules`} disabled={!enabled} className="flex w-full items-center justify-between gap-4 text-left disabled:cursor-not-allowed disabled:opacity-45" data-id="element-1224" data-mp="2poxcpr6vn4ex">
                <div data-id="element-1225" data-mp="38nt650awverp">
                  <p className="text-sm font-semibold text-ink" data-id="element-1226" data-mp="38obbcmfjx3br">{market.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-ink/55" data-id="element-1227" data-mp="33ogjafk1i7p4">
                    {enabled ? `${total - excludedList.length} of ${total} markets enabled` : `Enable ${market.label} above to manage individual markets`}
                  </p>
                </div>
                <ChevronDownIcon className={`h-4 w-4 shrink-0 text-ink/45 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1228" data-mp="1o8ho04viu66a" />
              </button>

              {open && enabled && <div id={`${market.id}-rules`} className="mt-5 rounded-2xl border border-ink/10 bg-cream/55 p-4 sm:p-5" data-id="element-1229" data-mp="3uxje854o3p9y">
                  <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-1230" data-mp="3qp47hrzjagq">
                    <p className="text-xs leading-relaxed text-ink/60" data-id="element-1231" data-mp="7qz88ow7vqsy">Deselect anywhere you do not want inventory sold. Greyed entries are excluded.</p>
                    <div className="flex gap-2" data-id="element-1232" data-mp="hhu5eob04at6">
                      <button type="button" onClick={() => setAllPlaces(market, true)} className="rounded-full border border-ink/15 px-2.5 py-1 text-[11px] font-semibold text-ink/65 transition-colors hover:bg-paper" data-id="element-1233" data-mp="hh28991ra50s">
                        Select all
                      </button>
                      <button type="button" onClick={() => setAllPlaces(market, false)} className="rounded-full border border-ink/15 px-2.5 py-1 text-[11px] font-semibold text-ink/65 transition-colors hover:bg-paper" data-id="element-1234" data-mp="2t868g203yvxz">
                        Clear all
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 space-y-5" data-id="element-1235" data-mp="dgh86ibuplzg">
                    {market.groups.map((group, index) => <div key={group.label ?? index} data-id="element-1236" data-mp="9ff9q3prddyj" data-mpi={index}>
                        {group.label && <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-1237" data-mp="nb0tnwh4x7k3">{group.label}</p>}
                        <div className={`flex flex-wrap gap-2 ${group.label ? 'mt-2.5' : ''}`} data-id="element-1238" data-mp="x36m17batqzt">
                          {group.places.map(place => {
                    const off = excludedList.includes(place);
                    return <button key={place} type="button" onClick={() => togglePlace(market.id, place)} aria-pressed={!off} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${off ? 'border-ink/10 bg-paper text-ink/40 hover:border-[#BB5E40]/35 hover:text-ink/65' : 'border-[#BB5E40]/35 bg-[#BB5E40]/5 text-[#94452E] hover:bg-[#BB5E40]/10'}`} data-id="element-1239" data-mp="1618rbz22f5sw">
                                {!off && <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-1240" data-mp="s2xu0q25optl" />}
                                {place}
                              </button>;
                  })}
                        </div>
                      </div>)}
                  </div>
                </div>}
            </div>;
      })}
      </div>
    </section>;
}